import { useApolloClient } from '@apollo/client/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { batchesOptions, homeBatchesOptions, trackableItemsOptions } from './useBatchTrackingQueries';
import type { Kitchen } from '../libs';
import useNow from './useNow';
import { dayInKitchen, defaultBatchRange } from '../libs/utils/kitchenDay';

/**
 * Loads what Home's Manual batch dialog and the Batch Tracking page open on, in the background: when KDS opens, and again when
 * the kitchen, the working day or the kitchen's day changes. A read that is still fresh is not asked again, and a failure is left
 * to the screen. The day follows `useNow`, as Home's does, so it rolls over at the kitchen's midnight.
 */
const usePrefetchBatchTracking = ({
  kitchen,
  date,
  canViewBatchTracking
}: {
  kitchen: Kitchen;
  date: string;
  canViewBatchTracking: boolean;
}) => {
  const client = useApolloClient();
  const queryClient = useQueryClient();
  const today = dayInKitchen(useNow(), kitchen);
  useEffect(() => {
    void queryClient.prefetchQuery(trackableItemsOptions(client, kitchen));
    void queryClient.prefetchQuery(homeBatchesOptions(client, kitchen, today));
    if (canViewBatchTracking) {
      void queryClient.prefetchInfiniteQuery(batchesOptions(client, kitchen, defaultBatchRange(date)));
    }
  }, [client, queryClient, kitchen, today, date, canViewBatchTracking]);
};

export default usePrefetchBatchTracking;
